import {produce} from 'immer';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  DatetimeSchemaFieldTypedValue,
  FloatSchemaFieldTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {DataParameterTypedValue, ProcedureDataParameterSetting} from 'procedures/ProcedureDomainTypes';
import {
  DataParameterFieldType,
  DataParameterType,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {
  doesFieldTypeSupportMinMax,
  makeDataParameterTypedValueFromText,
} from 'procedures/tables/procedureTableAdapterUtils';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

/**
 * Merges a library-derived field type with the existing setting's data type.
 * The library definition dictates the field type (FLOAT/TEXT/DATETIME), but the unit is configured
 * independently in the table and must be preserved.
 */
function mergeLibraryFieldTypeWithExistingUnit(
  libraryFieldType: DataParameterFieldType,
  existingDataType: DataParameterType
): DataParameterType {
  if (libraryFieldType === FieldType.FLOAT) {
    return {
      type: FieldType.FLOAT,
      requiredLink: null,
      unit: existingDataType.type === FieldType.FLOAT ? existingDataType.unit : null,
    };
  }
  return {type: libraryFieldType, requiredLink: null, unit: null};
}

/**
 * Converts a setpoint typed value to match the target field type.
 * Preserves the display text across type changes.
 */
function convertSetpointTypedValue(
  existing: DataParameterTypedValue,
  targetType: DataParameterFieldType
): DataParameterTypedValue {
  if (existing.type === targetType) {
    return existing;
  }
  return makeDataParameterTypedValueFromText(targetType, existing.value);
}

/**
 * Returns an empty (unpopulated) min/max typed value for the given field type,
 * or null if the type does not support min/max.
 */
function emptyMinMaxForType(
  fieldType: DataParameterFieldType
): FloatSchemaFieldTypedValue | DatetimeSchemaFieldTypedValue | null {
  switch (fieldType) {
    case FieldType.FLOAT:
      return {type: FieldType.FLOAT, value: null, rawValue: null, floatValue: null};
    case FieldType.DATETIME:
      return {type: FieldType.DATETIME, value: null, datetimeValue: null};
    default:
      return null;
  }
}

/**
 * Reconciles type-dependent fields (minimum, maximum, setpoint value) when the
 * resolved data type differs from what buildCoreDataParameterSetting inferred
 * from the (potentially stale) TYPE cell.
 */
function reconcileTypeDependentFields(setting: ProcedureDataParameterSetting): ProcedureDataParameterSetting {
  const newDataType = setting.dataParameterVersion.dataType;
  const supportsMinMax = doesFieldTypeSupportMinMax(newDataType.type);
  const {minimum, maximum} = setting.dataParameterVersion;
  const hasMinMax = minimum != null || maximum != null;
  // We can't convert between min/max values of different types, so when the type changes, we clear the value
  // but need to ensure it's the correct representation of a null value for the new type.
  const needsMinMaxReset = supportsMinMax
    ? minimum == null ||
      minimum.type !== newDataType.type ||
      maximum == null ||
      maximum.type !== newDataType.type
    : hasMinMax;
  const plannedValue = setting.values[0];
  assert(setting.values.length === 1, 'Expected only one setpoint value');
  const needsSetpointConvert = plannedValue != null && plannedValue.value.type !== newDataType.type;

  if (!needsMinMaxReset && !needsSetpointConvert) {
    return setting;
  }

  const emptyMinimum = emptyMinMaxForType(newDataType.type);
  const emptyMaximum = emptyMinMaxForType(newDataType.type);

  return {
    ...setting,
    dataParameterVersion: {
      ...setting.dataParameterVersion,
      ...(needsMinMaxReset ? {minimum: emptyMinimum, maximum: emptyMaximum} : {}),
    },
    ...(needsSetpointConvert
      ? {
          values: [
            {
              ...plannedValue,
              value: convertSetpointTypedValue(plannedValue.value, newDataType.type),
            },
          ],
        }
      : {}),
  };
}

/**
 * Applies cached field types to settings that have a library parameter definition.
 * Returns the updated settings.
 */
export function applyFieldTypesFromCache(
  settings: Array<ProcedureDataParameterSetting>,
  cache: Record<string, DataParameterFieldType>
): Array<ProcedureDataParameterSetting> {
  const updatedSettings = settings.map((setting) => {
    const libDef = setting.dataParameterVersion.libraryParameterDefinition;
    assert(
      BenchlingConfig.PROCEDURES_ENABLE_PARAMETER_LIBRARY_ENFORCEMENT,
      'Expected parameter library enforcement to be enabled'
    );
    // There must be a library parameter definition in order to submit the table. Just leave the setting alone
    // until a new library parameter definition is selected. This avoids showing errors in a transient state.
    if (libDef == null) {
      return setting;
    }
    const cachedFieldType = cache[libDef.id];
    // If the cache hasn't loaded yet, or the parameter definition isn't in the cache because it's archived,
    // leave the setting alone. An archived definition will block submission of the definitions tab.
    if (cachedFieldType == null) {
      return setting;
    }
    const newDataType = mergeLibraryFieldTypeWithExistingUnit(
      cachedFieldType,
      setting.dataParameterVersion.dataType
    );
    const withDataType = produce(setting, (draft) => {
      draft.dataParameterVersion.dataType = newDataType;
    });
    return reconcileTypeDependentFields(withDataType);
  });
  return updatedSettings;
}
